import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Checkbox } from 'is24-corecss'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import { RealEstateType } from '../../@types/realEstateType'
import { TextInput } from '../styled/TextInput/TextInput'
import { ReduxState } from '../../store/reducers'
import { updateAreaWithDefaultValues, updateLivingSpace, updatePlotArea } from '../../store/lead/leadSlice'
import { updatePropertyChecked } from '../../store/area/areaSlice'
import { livingSpaceLimits, plotAreaLimits, SpaceLimit, validateLivingSpace, validatePlotArea } from './utils'
import { formatNumberForLocaleWithoutDecimals } from '../../utils/formatting'

const plotAreaPlaceHolderMap = {
  [RealEstateType.HOUSE]: 'z.B. 800',
  [RealEstateType.LAND]: 'z.B. 1200',
  [RealEstateType.COMMERCIAL]: 'z.B. 1000',
}

const plotAreaMarginMap = {
  [RealEstateType.HOUSE]: 'margin-top-l',
  [RealEstateType.LAND]: '',
  [RealEstateType.COMMERCIAL]: 'margin-top-s',
}

export const getLivingSpaceLabel = (realEstateType: RealEstateType): string =>
  realEstateType === RealEstateType.COMMERCIAL ? 'Nutzfläche' : 'Wohnfläche'

const ERROR_MESSAGE_DELAY = 1000
const DEFAULT_COMMERCIAL_PLOT_AREA = 1000

export const getLivingSpaceErrorMessage = (livingSpaceLabel: string, livingSpaceLimits: SpaceLimit): string => {
  return `Bitte gib eine ${livingSpaceLabel} von ${formatNumberForLocaleWithoutDecimals(
    livingSpaceLimits.min
  )}-${formatNumberForLocaleWithoutDecimals(livingSpaceLimits.max)} m² an.`
}

interface AreaProps {
  onLivingSpaceChanged?: (livingSpace?: number) => void
  onPlotAreaChanged?: (plotArea?: number) => void
  className?: string
  autofocus?: boolean
}

const Area = ({ onPlotAreaChanged, onLivingSpaceChanged, className, autofocus }: AreaProps) => {
  const plotAreaRef = useRef<HTMLInputElement>(null)
  const {
    config: { theme },
    lead: { realEstateType: stateRealEstateType, livingSpace, plotArea },
    area: { propertyChecked },
  } = useSelector((state: ReduxState) => state)

  const [isValidLivingSpaceDelayed, setIsValidLivingSpaceDelayed] = useState<boolean>(true)
  const [isValidPlotAreaDelayed, setIsValidPlotAreaDelayed] = useState<boolean>(true)

  const realEstateType = stateRealEstateType ? stateRealEstateType : RealEstateType.HOUSE
  const plotAreaDisabled = realEstateType === RealEstateType.COMMERCIAL && !propertyChecked
  const livingSpaceLabel = getLivingSpaceLabel(realEstateType)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!(plotArea || livingSpace)) dispatch(updateAreaWithDefaultValues())
  }, [])

  const debouncedValidateLivingSpace = useCallback(
    debounce((newValue?: number) => {
      setIsValidLivingSpaceDelayed(
        realEstateType === RealEstateType.LAND || validateLivingSpace(realEstateType, newValue)
      )
      onLivingSpaceChanged && onLivingSpaceChanged(newValue)
    }, ERROR_MESSAGE_DELAY),
    []
  )

  const debouncedValidatePlotArea = useCallback(
    debounce((newValue?: number) => {
      setIsValidPlotAreaDelayed(
        realEstateType === RealEstateType.APARTMENT || plotAreaDisabled || validatePlotArea(realEstateType, newValue)
      )
      onPlotAreaChanged && onPlotAreaChanged(newValue)
    }, ERROR_MESSAGE_DELAY),
    [plotAreaDisabled]
  )

  useEffect(() => {
    debouncedValidateLivingSpace(livingSpace)
    return debouncedValidateLivingSpace.cancel
  }, [debouncedValidateLivingSpace, livingSpace])

  useEffect(() => {
    debouncedValidatePlotArea(plotArea)
    return debouncedValidatePlotArea.cancel
  }, [debouncedValidatePlotArea, plotArea, plotAreaDisabled])

  const livingSpaceChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value.toString().slice(0, 6))
    dispatch(updateLivingSpace(newValue))
    if (typeof onLivingSpaceChanged !== 'undefined') onLivingSpaceChanged(newValue)
  }

  const plotAreaChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(event.target.value.toString().slice(0, 5))
    dispatch(updatePlotArea(newValue))
    if (typeof onPlotAreaChanged !== 'undefined') onPlotAreaChanged(newValue)
  }

  const propertyCheckChanged = (event: ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      dispatch(updatePlotArea(undefined))
    } else {
      dispatch(updatePlotArea(DEFAULT_COMMERCIAL_PLOT_AREA))
      setTimeout(() => {
        const plotAreaInput = plotAreaRef.current
        plotAreaInput?.focus()
      }, 0)
    }

    dispatch(updatePropertyChecked(event.target.checked))
  }

  return (
    <div className='grid-flex grid-justify-center'>
      <div
        className={`grid-item one-whole align-left ${className}
         input-error-fade area-step`}
      >
        {realEstateType !== RealEstateType.LAND && (
          <TextInput
            id='living_space'
            data-testing='area-living-space-input'
            data-testid='area-living-space-input'
            affixRight='unit'
            unitRight='sqm'
            placeholder={realEstateType === RealEstateType.COMMERCIAL ? 'z.B. 200' : 'z.B. 180'}
            label={livingSpaceLabel}
            type='tel'
            maxLength={6}
            className={'input-text'}
            value={livingSpace || ''}
            onChange={livingSpaceChanged}
            valid={isValidLivingSpaceDelayed}
            errorMessage={getLivingSpaceErrorMessage(livingSpaceLabel, livingSpaceLimits[realEstateType])}
            textColor={theme.text}
            borderColor={theme.secondary}
            backgroundColor={theme.background}
            autoFocus={autofocus}
          />
        )}

        {realEstateType === RealEstateType.COMMERCIAL && (
          <div className='margin-top-s'>
            <Checkbox
              id='property_checkbox'
              data-testid='property_checkbox'
              label='Grundstück'
              onChange={propertyCheckChanged}
            />
          </div>
        )}

        {realEstateType !== RealEstateType.APARTMENT && (
          <div className={`${plotAreaMarginMap[realEstateType]} input-error-fade`}>
            <TextInput
              id='plot_area'
              data-testing='area-plot-area-input'
              data-testid='area-plot-area-input'
              affixRight='unit'
              unitRight='sqm'
              placeholder={plotAreaPlaceHolderMap[realEstateType]}
              maxLength={5}
              label='Grundstücksfläche'
              type='tel'
              value={plotArea || ''}
              onChange={plotAreaChanged}
              className={'input-text'}
              valid={isValidPlotAreaDelayed}
              errorMessage={`Bitte gib eine Grundstücksfläche von ${formatNumberForLocaleWithoutDecimals(
                plotAreaLimits[realEstateType].min
              )}-${formatNumberForLocaleWithoutDecimals(plotAreaLimits[realEstateType].max)} m² an.`}
              disabled={plotAreaDisabled}
              ref={plotAreaRef}
              textColor={theme.text}
              borderColor={theme.secondary}
              backgroundColor={theme.background}
              autoFocus={autofocus}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default Area
